import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { useAuth } from '../contexts/AuthContext'
import { usersService } from '../services/users'

const MfaRequiredPage = () => {
  const { t } = useTranslation('auth')
  const [step, setStep] = useState('info') // info, setup, verify, recovery-codes
  const [setupData, setSetupData] = useState(null)
  const [verifyCode, setVerifyCode] = useState('')
  const [recoveryCodes, setRecoveryCodes] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const { user, loading: authLoading, logout } = useAuth()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login', { replace: true })
    }
  }, [user, authLoading, navigate])

  const handleStartSetup = async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await usersService.setupMfa()
      setSetupData(data)
      setStep('setup')
    } catch (err) {
      setError(err.response?.data?.error || t('errors.failedToStartMfaSetup'))
    } finally {
      setLoading(false)
    }
  }

  const handleVerifySetup = async () => {
    if (!verifyCode || verifyCode.length !== 6) {
      setError(t('errors.enterSixDigitCode'))
      return
    }

    setLoading(true)
    setError(null)
    try {
      const result = await usersService.verifyMfa(verifyCode)
      setRecoveryCodes(result.recoveryCodes || [])
      setStep('recovery-codes')
      setVerifyCode('')
      // Invalidate MFA status cache
      queryClient.invalidateQueries({ queryKey: ['mfaStatus'] })
    } catch (err) {
      setError(err.response?.data?.error || t('errors.invalidCodeTryAgain'))
    } finally {
      setLoading(false)
    }
  }

  const handleComplete = () => {
    // Mark session as having passed MFA check
    sessionStorage.setItem('coformit_mfa_session_check', 'passed')
    // Navigate to dashboard - MFA is now enabled
    navigate('/dashboard', { replace: true })
  }

  const handleLogout = async () => {
    await logout()
    navigate('/login', { replace: true })
  }

  const copyRecoveryCodes = () => {
    const text = recoveryCodes.join('\n')
    navigator.clipboard.writeText(text)
  }

  if (authLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh'
      }}>
        <p>{t('common:buttons.loading')}</p>
      </div>
    )
  }

  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      padding: '1rem',
      backgroundColor: '#f5f5f5'
    }}>
      <div style={{
        width: '100%',
        maxWidth: '500px',
        padding: '2rem',
        backgroundColor: 'white',
        border: '1px solid var(--color-border)',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        {step === 'info' && (
          <>
            <h1 style={{ marginBottom: '1rem', textAlign: 'center' }}>
              {t('mfaRequired.title')}
            </h1>

            <div style={{
              padding: '1rem',
              backgroundColor: '#fff3cd',
              borderRadius: '4px',
              marginBottom: '1.5rem'
            }}>
              <p style={{ margin: 0 }}>
                {t('mfaRequired.description')}
              </p>
            </div>

            <p style={{ marginBottom: '1.5rem', color: '#666' }}>
              {t('mfaRequired.authenticatorAppsTitle')}
            </p>

            <ul style={{ marginBottom: '1.5rem', paddingLeft: '1.5rem', color: '#666' }}>
              <li>Google Authenticator</li>
              <li>Microsoft Authenticator</li>
              <li>Authy</li>
              <li>1Password</li>
            </ul>

            {error && (
              <div style={{
                padding: '1rem',
                backgroundColor: '#f8d7da',
                color: '#721c24',
                marginBottom: '1.5rem',
                borderRadius: '4px'
              }}>
                {error}
              </div>
            )}

            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'space-between' }}>
              <button
                type="button"
                onClick={handleLogout}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: 'var(--color-secondary)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                {t('mfaRequired.signOut')}
              </button>
              <button
                type="button"
                onClick={handleStartSetup}
                disabled={loading}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: 'var(--color-success)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.6 : 1
                }}
              >
                {loading ? t('mfaRequired.loading') : t('mfaRequired.setUpMfa')}
              </button>
            </div>
          </>
        )}

        {step === 'setup' && (
          <>
            <h1 style={{ marginBottom: '1rem', textAlign: 'center' }}>
              {t('mfaRequired.setupTitle')}
            </h1>

            {error && (
              <div style={{
                padding: '1rem',
                backgroundColor: '#f8d7da',
                color: '#721c24',
                marginBottom: '1.5rem',
                borderRadius: '4px'
              }}>
                {error}
              </div>
            )}

            <div style={{ marginBottom: '1.5rem' }}>
              <p style={{ marginBottom: '1rem' }}>
                <strong>Step 1:</strong> {t('mfaRequired.scanQrCode')}
              </p>

              {setupData?.qrCodeUrl && (
                <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
                  <img
                    src={setupData.qrCodeUrl}
                    alt={t('mfaRequired.qrCodeAlt')}
                    style={{ maxWidth: '200px', border: '1px solid #ddd', borderRadius: '4px' }}
                  />
                </div>
              )}

              <p style={{ fontSize: '0.875rem', color: '#666', marginBottom: '0.5rem' }}>
                {t('mfaRequired.enterCodeManually')}
              </p>
              <code style={{
                display: 'block',
                padding: '0.5rem',
                backgroundColor: '#f8f9fa',
                borderRadius: '4px',
                wordBreak: 'break-all',
                fontSize: '0.875rem'
              }}>
                {setupData?.secret}
              </code>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <p style={{ marginBottom: '0.5rem' }}>
                <strong>Step 2:</strong> {t('mfaRequired.enterVerificationCode')}
              </p>
              <input
                type="text"
                value={verifyCode}
                onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                autoComplete="one-time-code"
                inputMode="numeric"
                maxLength={6}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  fontSize: '1.25rem',
                  textAlign: 'center',
                  letterSpacing: '0.5rem',
                  border: '1px solid #ddd',
                  borderRadius: '4px'
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => {
                  setError(null)
                  setStep('info')
                }}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: 'var(--color-secondary)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                {t('mfaRequired.back')}
              </button>
              <button
                type="button"
                onClick={handleVerifySetup}
                disabled={loading || verifyCode.length !== 6}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: 'var(--color-success)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: loading || verifyCode.length !== 6 ? 'not-allowed' : 'pointer',
                  opacity: loading || verifyCode.length !== 6 ? 0.6 : 1
                }}
              >
                {loading ? t('mfa.verifying') : t('mfaRequired.verifyAndEnable')}
              </button>
            </div>
          </>
        )}

        {step === 'recovery-codes' && (
          <>
            <h1 style={{ marginBottom: '1rem', textAlign: 'center', color: 'var(--color-success)' }}>
              {t('mfaRequired.enabledTitle')}
            </h1>

            <div style={{
              padding: '1rem',
              backgroundColor: '#fff3cd',
              borderRadius: '4px',
              marginBottom: '1.5rem'
            }}>
              <strong>{t('common:labels.important')}:</strong> {t('mfaRequired.saveRecoveryCodes')}
            </div>

            <div style={{
              padding: '1rem',
              backgroundColor: '#f8f9fa',
              borderRadius: '4px',
              marginBottom: '1.5rem',
              fontFamily: 'monospace'
            }}>
              {recoveryCodes.map((code, index) => (
                <div key={index} style={{ padding: '0.25rem 0' }}>
                  {code}
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={copyRecoveryCodes}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: '#17a2b8',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                {t('mfaRequired.copyCodes')}
              </button>
              <button
                type="button"
                onClick={handleComplete}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: 'var(--color-success)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                {t('mfaRequired.continueToDashboard')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default MfaRequiredPage
